<script lang="ts">
	import type { SearchMethodParams, SearchResponse } from "algoliasearch";
	import type { Hit, LiteClient } from "algoliasearch/lite";
	import { Category } from "@core/schema/Category.js";
	import type { Indexable } from "@core/schema/Indexable.js";
	import type { IndexableTable } from "@core/schema/indexableTables.js";
	import { categoryNameMap } from "@core/schema/names/categoryNameMap.js";
	import { getProductPath } from "@core/schema/paths/getProductPath.js";
	import { getAlgoliaIndexName } from "@core/schema/utils/getAlgoliaIndexName.js";
	import { sameValueForEveryLocale } from "@core/utils/sameValueForEveryLocale.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import Formatted from "./common/Formatted.svelte";

	const { searchText }: { searchText: string } = $props();

	const { tenant } = getGlobalTemplateProps();
	const detailPathGetterMap: { [K in IndexableTable]: (hit: Hit<Indexable<K>>) => string } = {
		product: (hit) => getProductPath(tenant, { name: sameValueForEveryLocale(hit.name) }),
	};

	let nextSearch = "";
	let canMakeRequest = true;
	let results = $state.raw<SearchResponse<Indexable>[]>([]);
	let client: LiteClient | undefined;

	const { ALGOLIA_API_SEARCH_KEY, ALGOLIA_APPLICATION_ID, PROJECT, ENV } = getGlobalSettings();

	const search = createPromiseWithStatus(async (params: SearchMethodParams) => {
		if (!client) {
			const { liteClient } = await import("algoliasearch/lite");
			// eslint-disable-next-line require-atomic-updates
			client = liteClient(ALGOLIA_APPLICATION_ID, ALGOLIA_API_SEARCH_KEY);
		}
		return client.searchForHits<Indexable>(params);
	});

	async function onSearchTextChange(text: string): Promise<void> {
		nextSearch = text;
		if (!canMakeRequest) {
			return;
		}

		if (text.length <= 2) {
			results = [];
			return;
		}

		try {
			canMakeRequest = false;
			({ results } = await search.call({
				requests: [
					{
						indexName: getAlgoliaIndexName(ENV, PROJECT, tenant.locale),
						query: text,
					},
				],
			}));
		} finally {
			window.setTimeout(() => {
				canMakeRequest = true;
				if (nextSearch !== text) {
					void onSearchTextChange(nextSearch);
				}
			}, 250);
		}
	}

	function getHighlightedText<T extends Indexable>(hit: T, key: Extract<keyof T, string>): string {
		const { _highlightResult } = hit;
		if (!_highlightResult) {
			return "";
		}
		const property = _highlightResult[key];
		if (!property || !("value" in property) || typeof property.value !== "string") {
			return "";
		}

		return property.value;
	}

	$effect(() => {
		void onSearchTextChange(searchText);
	});

	const hits = $derived(results.flatMap(({ hits }) => hits));

	/* eslint-disable svelte/no-at-html-tags */
</script>

<div class="flex flex-col">
	{#if searchText.length <= 2}
		<div class="p-4 whitespace-nowrap">
			<Formatted text="Výsledky se zobrazí zde…" />
		</div>
	{:else if hits.length === 0}
		<div class="p-4 whitespace-nowrap">
			<Formatted text="Váš dotaz neodpovídá žádnému ze záznamů." />
		</div>
	{:else}
		{@const perCategory = Object.values(Category)
			.map((category) => ({
				category,
				items: hits.filter((hit) => hit._data.category === category),
			}))
			.filter(({ items }) => items.length > 0)}
		{#each perCategory as { category, items }, index (category)}
			<div class="flex flex-col">
				<p class={["sticky top-0 p-4 font-semibold border-b border-blue text-blue bg-white", index > 0 && "mt-6"]}>
					{categoryNameMap[category][tenant.locale]}
				</p>
				<div class="flex flex-col">
					{#each items as hit (hit.objectID)}
						{@const [tag] = hit._tags}
						<a
							class="flex p-4 gap-2 items-center hover:bg-lightblue/50 transition-all duration-300 overflow-hidden"
							href={detailPathGetterMap[tag](hit)}
						>
							{#if hit.image}
								<img
									src={hit.image}
									alt={hit.name}
									width={48}
									height={48}
									class="rounded size-12 shrink-0 border object-contain"
								/>
							{/if}
							<div class="flex flex-col grow w-full gap-1">
								<div class="truncate [&_em]:font-semibold [&_em]:not-italic [&_em]:text-blue">
									{@html getHighlightedText(hit, "name")}
								</div>
								<div class="text-xs truncate [&_em]:font-semibold [&_em]:not-italic [&_em]:text-blue">
									{@html getHighlightedText(hit, "content")}
								</div>
							</div>
						</a>
					{/each}
				</div>
			</div>
		{/each}
	{/if}
</div>
